import { HUB_ICON_H, HUB_ICON_W } from "./hubIconTypes.js";

/** Reference draw color in the editor (not stored in the published mask). */
export const HUB_ICON_EDITOR_DRAW = "#ffffff";

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

/** Perceived luminance 0–255 from RGB. */
export function pixelLuminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Convert imported image pixels to a grayscale luminance mask:
 * white RGB with alpha = brightness × source opacity (shadows → lower alpha).
 */
export function imageDataToLuminanceMask(src: ImageData): ImageData {
  const out = new ImageData(src.width, src.height);
  for (let i = 0; i < src.data.length; i += 4) {
    const r = src.data[i]!;
    const g = src.data[i + 1]!;
    const b = src.data[i + 2]!;
    const srcA = src.data[i + 3]!;
    if (srcA <= 0) continue;
    const lum = pixelLuminance(r, g, b);
    const alpha = clampByte((lum / 255) * srcA);
    if (alpha <= 0) continue;
    out.data[i] = 255;
    out.data[i + 1] = 255;
    out.data[i + 2] = 255;
    out.data[i + 3] = alpha;
  }
  return out;
}

/** @deprecated Use imageDataToLuminanceMask */
export function imageDataToSilhouetteMask(src: ImageData): ImageData {
  return imageDataToLuminanceMask(src);
}

/** Ensure stored mask is white RGB with preserved alpha tones. */
export function normalizeMaskImageData(src: ImageData): ImageData {
  const out = new ImageData(src.width, src.height);
  for (let i = 0; i < src.data.length; i += 4) {
    const a = src.data[i + 3]!;
    if (a <= 0) continue;
    out.data[i] = 255;
    out.data[i + 1] = 255;
    out.data[i + 2] = 255;
    out.data[i + 3] = a;
  }
  return out;
}

export function maskImageDataEmpty(src: ImageData): boolean {
  for (let i = 3; i < src.data.length; i += 4) {
    if (src.data[i]! > 0) return false;
  }
  return true;
}

/** Shift visible mask pixels so their bounding box is centered (size preserved). */
export function centerMaskImageData(src: ImageData): ImageData {
  const w = src.width;
  const h = src.height;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (src.data[(y * w + x) * 4 + 3]! <= 0) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < minX) return src;
  const bw = maxX - minX + 1;
  const bh = maxY - minY + 1;
  const ox = Math.floor((w - bw) / 2) - minX;
  const oy = Math.floor((h - bh) / 2) - minY;
  const out = new ImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const si = (y * w + x) * 4;
      const a = src.data[si + 3]!;
      if (a <= 0) continue;
      const nx = x + ox;
      const ny = y + oy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const di = (ny * w + nx) * 4;
      out.data[di] = src.data[si]!;
      out.data[di + 1] = src.data[si + 1]!;
      out.data[di + 2] = src.data[si + 2]!;
      out.data[di + 3] = a;
    }
  }
  return out;
}

export function cloneMaskImageData(src: ImageData): ImageData {
  return new ImageData(new Uint8ClampedArray(src.data), src.width, src.height);
}

export function maskDataUrlFromImageData(img: ImageData): string {
  const c = document.createElement("canvas");
  c.width = img.width;
  c.height = img.height;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");
  ctx.putImageData(img, 0, 0);
  return c.toDataURL("image/png");
}

export function loadImageDataFromDataUrl(dataUrl: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      if (!ctx) {
        reject(new Error("canvas_unavailable"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(ctx.getImageData(0, 0, w, h));
    };
    img.onerror = () => reject(new Error("invalid_image"));
    img.src = dataUrl;
  });
}

/** Nearest-neighbor downscale preserving aspect ratio into the hub icon canvas. */
export function downscaleImageToHubIconGrid(img: HTMLImageElement, targetW: number, targetH: number): ImageData {
  const c = document.createElement("canvas");
  c.width = targetW;
  c.height = targetH;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, targetW, targetH);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const scale = Math.min(targetW / iw, targetH / ih);
  const w = Math.max(1, Math.round(iw * scale));
  const h = Math.max(1, Math.round(ih * scale));
  ctx.drawImage(img, Math.round((targetW - w) / 2), Math.round((targetH - h) / 2), w, h);
  return ctx.getImageData(0, 0, targetW, targetH);
}

export const HUB_ICON_RESOLUTION_CANDIDATES = [
  { w: 50, h: 70 },
  { w: 100, h: 140 },
  { w: 150, h: 210 },
  { w: 200, h: 280 },
] as const;

/** Scale any mask ImageData into the current hub canvas size (fills the card). */
export function fitMaskImageDataToHubCanvas(src: ImageData): ImageData {
  if (src.width === HUB_ICON_W && src.height === HUB_ICON_H) {
    return normalizeMaskImageData(src);
  }
  const srcCanvas = document.createElement("canvas");
  srcCanvas.width = src.width;
  srcCanvas.height = src.height;
  const sctx = srcCanvas.getContext("2d")!;
  sctx.putImageData(src, 0, 0);
  const dest = document.createElement("canvas");
  dest.width = HUB_ICON_W;
  dest.height = HUB_ICON_H;
  const dctx = dest.getContext("2d")!;
  dctx.imageSmoothingEnabled = false;
  dctx.clearRect(0, 0, HUB_ICON_W, HUB_ICON_H);
  dctx.drawImage(srcCanvas, 0, 0, HUB_ICON_W, HUB_ICON_H);
  return normalizeMaskImageData(dctx.getImageData(0, 0, HUB_ICON_W, HUB_ICON_H));
}

export function hubIconResolutionOptions(): Array<{ w: number; h: number; label: string }> {
  return HUB_ICON_RESOLUTION_CANDIDATES.map(({ w, h }) => ({
    w,
    h,
    label: `${w}×${h}`,
  }));
}

export function defaultHubIconResolution(): { w: number; h: number } {
  return { w: HUB_ICON_W, h: HUB_ICON_H };
}
